import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Eye, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MathText } from '@/components/RenderContent';
import { confirmDialog } from '@/components/ConfirmDialog';
import { opsApi, apiError, fmtNum, FONT_HEAD, GRAD, Pagination } from './opsApi';
import Loader from '../../components/Loader';

// Browse the shared exam-prep question bank, view a question, and
// publish / unpublish it (backend: /api/ops/qb/questions*). Students only see
// published questions, so unpublishing hides it from every institute.

const STATUS_STYLE = {
  published: 'bg-green-50 text-green-700 border-green-200',
  draft: 'bg-slate-100 text-slate-600 border-slate-200',
  flagged: 'bg-amber-50 text-amber-700 border-amber-200',
};
const OPTION_LETTERS = ['A', 'B', 'C', 'D'];
const SEARCH_DEBOUNCE_MS = 350;
const selectCls = 'h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm';

const examLabel = (exam) => String(exam || '').replace('_', ' ');

function StatusBadge({ status }) {
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border capitalize ${STATUS_STYLE[status] || STATUS_STYLE.draft}`}>{status}</span>;
}

function QuestionDialog({ questionId, onClose, onChanged }) {
  const [q, setQ] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setQ(null);
    if (!questionId) return;
    opsApi.get(`/qb/questions/${questionId}`).then((r) => setQ(r.data))
      .catch((e) => { toast.error(apiError(e)); onClose(); });
  }, [questionId, onClose]);

  const setStatus = async (status) => {
    if (status === 'draft') {
      const ok = await confirmDialog({
        title: 'Unpublish question?',
        message: 'Students in every institute will stop seeing it in Exam Prep, and it is removed from the vector index.',
        confirmLabel: 'Unpublish',
      });
      if (!ok) return;
    }
    setSaving(true);
    try {
      const { data } = await opsApi.post(`/qb/questions/${questionId}/status`, { status });
      setQ(data);
      toast.success(status === 'published' ? 'Question published' : 'Question unpublished');
      onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const correct = String(q?.correct_answer || '').trim().toUpperCase();

  return (
    <Dialog open={!!questionId} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="qb-question-dialog">
        <DialogHeader>
          <DialogTitle style={FONT_HEAD}>Question</DialogTitle>
        </DialogHeader>
        {!q ? (
          <Loader />
        ) : (
          <div className="space-y-4 text-sm">
            <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500">
              <StatusBadge status={q.status} />
              <span className="font-medium text-slate-700">{examLabel(q.exam)} {q.year}</span>
              <span>· {q.subject_name || '—'} › {q.chapter_name || '—'}</span>
              <span className="capitalize">· {q.difficulty}</span>
              <span>· {String(q.question_type || '').replace('_', '-')}</span>
              <span className="font-mono">· {q.source_ref}</span>
            </div>
            {q.status === 'flagged' && q.flag_reason && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Flagged: {q.flag_reason}</p>
            )}

            <div className="leading-relaxed"><MathText text={q.question_latex} /></div>

            {Array.isArray(q.options_latex) && q.options_latex.length > 0 ? (
              <ol className="space-y-2">
                {q.options_latex.map((opt, i) => {
                  const isCorrect = OPTION_LETTERS[i] === correct;
                  return (
                    <li key={i} className={`flex gap-2 rounded-lg border px-3 py-2 ${isCorrect ? 'border-green-300 bg-green-50' : 'border-slate-200'}`}>
                      <span className={`font-semibold ${isCorrect ? 'text-green-700' : 'text-slate-400'}`}>{OPTION_LETTERS[i]}.</span>
                      <span className="flex-1"><MathText text={opt} /></span>
                      {isCorrect && <span className="text-[11px] font-semibold text-green-700">Correct</span>}
                    </li>
                  );
                })}
              </ol>
            ) : (
              <p><span className="text-slate-500">Answer:</span> <span className="font-semibold"><MathText text={q.correct_answer} /></span></p>
            )}

            {q.solution_latex && (
              <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                <p className="text-xs font-semibold text-slate-500 mb-1">Solution</p>
                <div className="leading-relaxed"><MathText text={q.solution_latex} /></div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              {q.status === 'published' && (
                <Button onClick={() => setStatus('draft')} disabled={saving} variant="outline"
                  className="rounded-xl text-red-600 border-red-200" data-testid="qb-unpublish-btn">Unpublish</Button>
              )}
              {q.status === 'draft' && (
                <Button onClick={() => setStatus('published')} disabled={saving}
                  className="rounded-xl text-white border-0" style={{ background: GRAD }} data-testid="qb-publish-btn">Publish</Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * @param {{ exams: string[], years: number[], onChanged: () => void }} props
 */
export default function OpsQBQuestions({ exams, years, onChanged }) {
  const [filters, setFilters] = useState({ exam: '', year: '', status: '', search: '' });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    const params = { page, page_size: 25 };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    opsApi.get('/qb/questions', { params }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [filters, page]);

  useEffect(() => {
    const t = setTimeout(load, filters.search ? SEARCH_DEBOUNCE_MS : 0);
    return () => clearTimeout(t);
  }, [load, filters.search]);

  const setFilter = (key, value) => { setFilters((f) => ({ ...f, [key]: value })); setPage(1); };
  const closeDialog = useCallback(() => setOpenId(null), []);
  const changed = useCallback(() => { load(); onChanged(); }, [load, onChanged]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="qb-questions">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3 flex-wrap">
        <h2 className="font-semibold mr-auto" style={FONT_HEAD}>Questions{data ? ` · ${fmtNum(data.total)}` : ''}</h2>
        <select aria-label="Exam" value={filters.exam} onChange={(e) => setFilter('exam', e.target.value)} className={selectCls}>
          <option value="">All exams</option>
          {exams.map((x) => <option key={x} value={x}>{examLabel(x)}</option>)}
        </select>
        <select aria-label="Year" value={filters.year} onChange={(e) => setFilter('year', e.target.value)} className={selectCls}>
          <option value="">All years</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select aria-label="Status" value={filters.status} onChange={(e) => setFilter('status', e.target.value)} className={selectCls}>
          <option value="">Any status</option>
          <option value="published">Published</option>
          <option value="draft">Draft (unpublished)</option>
          <option value="flagged">Flagged</option>
        </select>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={filters.search} onChange={(e) => setFilter('search', e.target.value)}
            placeholder="Search text or source_ref" className="pl-9 h-9 w-60" data-testid="qb-question-search" />
        </div>
      </div>

      {!data ? (
        <Loader />
      ) : data.items.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-10">No questions match.</p>
      ) : (
        <>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-100 text-left text-xs text-slate-400 uppercase tracking-wide">
              <th className="px-5 py-3 font-medium">Question</th>
              <th className="px-4 py-3 font-medium">Exam</th>
              <th className="px-4 py-3 font-medium">Chapter</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-5 py-3" />
            </tr></thead>
            <tbody>
              {data.items.map((q) => (
                <tr key={q.id} className="border-b border-slate-50 align-top">
                  <td className="px-5 py-3 max-w-md">
                    <div className="line-clamp-2 text-slate-800"><MathText text={q.question_latex} /></div>
                    <div className="text-[11px] text-slate-400 font-mono mt-0.5">{q.source_ref} · {q.difficulty} · {String(q.question_type || '').replace('_', '-')}</div>
                  </td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">{examLabel(q.exam)} {q.year}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{q.subject_name} › {q.chapter_name}</td>
                  <td className="px-4 py-3"><StatusBadge status={q.status} /></td>
                  <td className="px-5 py-3 text-right">
                    <Button size="sm" variant="outline" className="h-8 rounded-lg text-xs" onClick={() => setOpenId(q.id)} data-testid={`qb-view-${q.id}`}>
                      <Eye className="w-3.5 h-3.5 mr-1" /> View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination data={data} onPage={setPage} />
        </>
      )}

      <QuestionDialog questionId={openId} onClose={closeDialog} onChanged={changed} />
    </div>
  );
}
